import { message } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import useParentServices from '../../../../../../Statistics/queries/useParentServices';
import useServices from '../../../../../../Statistics/queries/useServices';
import { workScheduleQueryKeys } from '../../../api/queryKeys';
import { useWorkScheduleExecutersQuery } from '../../../api/useWorkScheduleExecutersQuery';
import { useWorkScheduleServicesWithTreeQuery } from '../../../api/useWorkScheduleServicesWithTreeQuery';
import { useWorkScheduleServiceNamesQuery } from '../../../api/useWorkScheduleServiceNamesQuery';
import {
  createWorkScheduleServiceSet,
  deleteWorkScheduleServiceSet,
  updateWorkScheduleServiceSet,
} from '../../../api/workScheduleApi';
import { ensureSuccessResponse } from '../../../api/workScheduleApiResponse';
import { getSelectedServiceTreeDetails } from '../../../model/workScheduleServiceTree';
import {
  entityModes,
  workerRoleOptions,
  workerFilterFieldNames,
  serviceSetFilterFieldNames,
} from '../CreateNewWorkScheduleByPeriod.constants';
import { getOption } from '../CreateNewWorkScheduleByPeriod.helpers';
import {
  getArrayValue,
  getReasonKeysFromServiceSet,
  getSelectionKey,
  getNormalizedServiceSetSelection,
  getServiceName,
  getServiceOption,
  getServiceReasonKey,
  getTrimmedWorkerText,
  getUniqueServiceSets,
  getUniqueValues,
  getValidSelectedValues,
  normalizeWorker,
} from './useScheduleEntities.utils';

const getServiceSetNameKey = (name) =>
  String(name || '')
    .trim()
    .toLocaleLowerCase('ru-RU');

const findCreatedServiceSet = ({
  existingServiceSetIds,
  serviceSetName,
  serviceSets = [],
}) => {
  const serviceSetNameKey = getServiceSetNameKey(serviceSetName);
  const serviceSetsWithSameName = serviceSets.filter(
    (serviceSet) => getServiceSetNameKey(serviceSet.name) === serviceSetNameKey
  );
  const createdServiceSet = serviceSetsWithSameName.find(
    (serviceSet) => !existingServiceSetIds.has(String(serviceSet.id))
  );

  return (
    createdServiceSet ||
    serviceSetsWithSameName[serviceSetsWithSameName.length - 1] ||
    null
  );
};

export const useScheduleEntities = ({
  companyId,
  form,
  initialValues = {},
  onDirtyChange,
}) => {
  const queryClient = useQueryClient();
  const { getFieldValue, setFieldsValue } = form;
  const [selectedWorkerIds, setSelectedWorkerIds] = useState(
    initialValues.workerIds || []
  );
  const [selectedServiceSets, setSelectedServiceSets] = useState(
    getUniqueValues(initialValues.serviceSetIds || [])
  );
  const [createdServiceSets, setCreatedServiceSets] = useState(
    () => initialValues.serviceSets || []
  );
  const [serviceSetMode, setServiceSetMode] = useState(entityModes.select);
  const workerSpecialityFilter =
    getFieldValue(workerFilterFieldNames.speciality) || [];
  const workerRoleFilter = getFieldValue(workerFilterFieldNames.role) || [];
  const selectedReasonIds = getArrayValue(
    getFieldValue('newServiceSetReasonIds')
  );
  const selectedReasonIdsKey = getSelectionKey(selectedReasonIds);
  const serviceNamesQuery = useWorkScheduleServiceNamesQuery(
    { companyId },
    {
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );
  const selectedServiceSetId = selectedServiceSets[0];
  const serviceDetailsQuery = useWorkScheduleServicesWithTreeQuery(
    { workScheduleServiceNameId: selectedServiceSetId },
    {
      enabled:
        Boolean(selectedServiceSetId) && serviceSetMode === entityModes.select,
      onError: () => {
        message.error('Не удалось загрузить услуги и причины набора');
      },
    }
  );
  const selectedServiceDetails = useMemo(
    () =>
      serviceDetailsQuery.isSuccess && !serviceDetailsQuery.isFetching
        ? getSelectedServiceTreeDetails(serviceDetailsQuery.data)
        : [],
    [
      serviceDetailsQuery.data,
      serviceDetailsQuery.isFetching,
      serviceDetailsQuery.isSuccess,
    ]
  );
  const getServiceSetWithDetails =
    (serviceSetId, details = []) =>
    (serviceSet = {}) => {
      if (String(serviceSet.id) !== String(serviceSetId)) {
        return serviceSet;
      }

      return {
        ...serviceSet,
        services: details,
      };
    };
  const serviceNamesQueryKey = workScheduleQueryKeys.serviceNames(companyId);
  const serviceSetOptions = useMemo(
    () =>
      getUniqueServiceSets([
        ...createdServiceSets,
        ...(serviceNamesQuery.data || []),
      ]).map((serviceSet) => ({
        ...getOption({
          id: serviceSet.id,
          name: serviceSet.name,
        }),
        comment: serviceSet.comment,
      })),
    [createdServiceSets, serviceNamesQuery.data]
  );
  const serviceSets = useMemo(() => {
    const baseServiceSets = getUniqueServiceSets([
      ...createdServiceSets,
      ...(serviceNamesQuery.data || []),
    ]);

    if (!selectedServiceSetId) {
      return baseServiceSets;
    }

    return baseServiceSets.map(
      getServiceSetWithDetails(selectedServiceSetId, selectedServiceDetails)
    );
  }, [
    createdServiceSets,
    selectedServiceDetails,
    selectedServiceSetId,
    serviceNamesQuery.data,
  ]);
  const selectedServiceSetItems = useMemo(
    () =>
      selectedServiceSets
        .map((serviceSetId) =>
          serviceSets.find(
            (serviceSet) => String(serviceSet.id) === String(serviceSetId)
          )
        )
        .filter(Boolean),
    [selectedServiceSets, serviceSets]
  );
  const executersQuery = useWorkScheduleExecutersQuery(
    { companyId },
    {
      onError: () => message.error('Не удалось загрузить исполнителей'),
    }
  );
  const workers = useMemo(
    () =>
      [...(executersQuery.data || []), ...(initialValues.workers || [])]
        .map(normalizeWorker)
        .filter(
          (worker) =>
            worker.id !== undefined && worker.id !== null && worker.fullName
        ),
    [executersQuery.data, initialValues.workers]
  );
  const workerSpecialityOptions = useMemo(() => {
    const specialities = [
      ...new Set(
        workers
          .flatMap((worker) => worker.specialities || [])
          .map(getTrimmedWorkerText)
          .filter(Boolean)
      ),
    ].sort((first, second) => first.localeCompare(second));

    return specialities.map((speciality) =>
      getOption({
        id: speciality,
        name: speciality,
      })
    );
  }, [workers]);
  const filteredWorkers = useMemo(() => {
    const specialityFilterValues = Array.isArray(workerSpecialityFilter)
      ? workerSpecialityFilter
      : [workerSpecialityFilter].filter(Boolean);
    const roleFilterValues = Array.isArray(workerRoleFilter)
      ? workerRoleFilter
      : [workerRoleFilter].filter(Boolean);

    return workers.filter((worker) => {
      const hasSpeciality =
        !specialityFilterValues.length ||
        worker.specialities?.some((speciality) =>
          specialityFilterValues.includes(speciality)
        );
      const hasRole =
        !roleFilterValues.length || roleFilterValues.includes(worker.role);

      return hasSpeciality && hasRole;
    });
  }, [workerRoleFilter, workerSpecialityFilter, workers]);
  const getWorkerOption = (worker) => ({
    ...getOption({
      id: worker.id,
      name: worker.fullName,
      content: worker.speciality
        ? `${worker.fullName} (${worker.speciality})`
        : worker.fullName,
    }),
    search: [worker.fullName, ...(worker.specialities || [])].join(' '),
    data: {
      speciality: worker.speciality,
      role: worker.role,
    },
  });
  const workerOptions = useMemo(
    () => filteredWorkers.map(getWorkerOption),
    [filteredWorkers]
  );
  const selectedWorkers = useMemo(
    () =>
      selectedWorkerIds
        .map((workerId) =>
          workers.find((worker) => String(worker.id) === String(workerId))
        )
        .filter(Boolean),
    [selectedWorkerIds, workers]
  );
  const newServiceSetName = getFieldValue('newServiceSetName') || '';
  const parentServicesQuery = useParentServices({
    companies: companyId ? [companyId] : undefined,
    options: {
      staleTime: Infinity,
    },
  });
  const serviceOptions = useMemo(
    () => (parentServicesQuery.data || []).map(getServiceOption),
    [parentServicesQuery.data]
  );
  const selectedServiceIds = useMemo(
    () => serviceOptions.map((service) => service.value),
    [serviceOptions]
  );
  const reasonsQuery = useServices(selectedServiceIds, {
    staleTime: Infinity,
  });
  const reasonOptions = useMemo(
    () =>
      (reasonsQuery.data || []).map((reason) => ({
        ...getServiceOption(reason),
        search: [getServiceName(reason), reason.parentName]
          .filter(Boolean)
          .join(' '),
        data: {
          isImmediate: reason.isImmediate,
          parentId: reason.parentId,
          parentName: reason.parentName,
        },
      })),
    [reasonsQuery.data]
  );
  const serviceReasonGroups = useMemo(
    () =>
      serviceOptions.map((service) => {
        const reasons = reasonOptions
          .filter(
            (reason) => String(reason.data?.parentId) === String(service.value)
          )
          .map((reason) => ({
            ...reason,
            serviceId: service.value,
            serviceName: service.text,
            value: getServiceReasonKey(service.value, reason.value),
            reasonId: reason.value,
          }));

        return {
          ...service,
          reasonKeys: reasons.map((reason) => reason.value),
          reasons,
        };
      }),
    [reasonOptions, serviceOptions]
  );
  const serviceReasonOptions = useMemo(
    () =>
      serviceReasonGroups.flatMap((service) =>
        service.reasons.map((reason) => ({
          ...reason,
          serviceId: service.value,
          serviceName: service.text,
        }))
      ),
    [serviceReasonGroups]
  );
  const isCreateServiceSetMode = serviceSetMode === entityModes.create;
  const isEditServiceSetMode = serviceSetMode === entityModes.edit;
  const isDeleteServiceSetMode = serviceSetMode === entityModes.delete;
  const invalidateServiceSets = () =>
    queryClient.invalidateQueries(serviceNamesQueryKey);

  const createServiceSetMutation = useMutation(
    async (serviceSet) => {
      const response = await createWorkScheduleServiceSet({
        companyId,
        serviceSetName: serviceSet.name,
        serviceSetComment: serviceSet.comment,
        requestParentTypeId: serviceSet.requestParentTypeId,
        requestTypeId: serviceSet.requestTypeId,
      });

      return ensureSuccessResponse(response, 'Не удалось создать набор услуг');
    },
    {
      onError: () => {
        message.error('Не удалось создать набор услуг');
      },
    }
  );
  const updateServiceSetMutation = useMutation(
    async (serviceSet) => {
      const response = await updateWorkScheduleServiceSet({
        companyId,
        id: serviceSet.id,
        serviceSetName: serviceSet.name,
        serviceSetComment: serviceSet.comment,
        requestParentTypeId: serviceSet.requestParentTypeId,
        requestTypeId: serviceSet.requestTypeId,
      });

      return ensureSuccessResponse(response, 'Не удалось изменить набор услуг');
    },
    {
      onError: () => {
        message.error('Не удалось изменить набор услуг');
      },
    }
  );
  const deleteServiceSetMutation = useMutation(
    async ({ id }) => {
      const response = await deleteWorkScheduleServiceSet({ id });

      return ensureSuccessResponse(response, 'Не удалось удалить набор услуг');
    },
    {
      onError: () => {
        message.error('Не удалось удалить набор услуг');
      },
    }
  );

  useEffect(() => {
    const workerId = getFieldValue('workerId');
    const hasSelectedWorker = workerOptions.some(
      (option) => String(option.value) === String(workerId)
    );

    if (workerId !== undefined && workerId !== null && !hasSelectedWorker) {
      setFieldsValue({ workerId: undefined });
    }
  }, [getFieldValue, setFieldsValue, workerOptions]);

  useEffect(() => {
    if (
      serviceSetMode !== entityModes.create ||
      !serviceOptions.length ||
      reasonsQuery.isFetching
    ) {
      return;
    }

    const validReasonIds = getValidSelectedValues(
      selectedReasonIds,
      serviceReasonOptions
    );

    if (getSelectionKey(validReasonIds) !== selectedReasonIdsKey) {
      setFieldsValue({ newServiceSetReasonIds: validReasonIds });
    }
  }, [
    serviceOptions.length,
    selectedReasonIds,
    selectedReasonIdsKey,
    serviceReasonOptions,
    serviceSetMode,
    reasonsQuery.isFetching,
    setFieldsValue,
  ]);

  const handleAddWorker = (workerIdArg) => {
    const workerId = workerIdArg ?? getFieldValue('workerId');
    const hasWorker = workerOptions.some(
      (option) => String(option.value) === String(workerId)
    );

    if (
      !workerId ||
      !hasWorker ||
      selectedWorkerIds.some((id) => String(id) === String(workerId))
    ) {
      return;
    }

    setSelectedWorkerIds((prevState) => [...prevState, workerId]);
    if (typeof onDirtyChange === 'function') {
      onDirtyChange(true);
    }
    setFieldsValue({ workerId: undefined });
  };

  const handleAddWorkers = (workerIds = []) => {
    const availableWorkerIds = new Set(
      workerOptions.map((option) => String(option.value))
    );
    const workerIdsToAdd = workerIds.filter(
      (workerId) =>
        availableWorkerIds.has(String(workerId)) &&
        !selectedWorkerIds.some((id) => String(id) === String(workerId))
    );

    if (!workerIdsToAdd.length) {
      return;
    }

    setSelectedWorkerIds((prevState) => [...prevState, ...workerIdsToAdd]);
    if (typeof onDirtyChange === 'function') {
      onDirtyChange(true);
    }
    setFieldsValue({ workerId: undefined });
  };

  const handleDeleteWorker = (workerId) => {
    setSelectedWorkerIds((prevState) => {
      const nextState = prevState.filter(
        (id) => String(id) !== String(workerId)
      );

      if (typeof onDirtyChange === 'function') {
        onDirtyChange(Boolean(nextState.length || selectedServiceSets.length));
      }

      return nextState;
    });
  };

  const handleDeleteWorkers = (workerIds = []) => {
    const workerIdsToDelete = workerIds.map(String);

    setSelectedWorkerIds((prevState) => {
      const nextState = prevState.filter(
        (id) => !workerIdsToDelete.includes(String(id))
      );

      if (typeof onDirtyChange === 'function') {
        onDirtyChange(Boolean(nextState.length || selectedServiceSets.length));
      }

      return nextState;
    });
  };

  const handleServiceSetsChange = (value) => {
    const nextValue = getUniqueValues(value);

    setSelectedServiceSets(nextValue);
    if (typeof onDirtyChange === 'function') {
      onDirtyChange(Boolean(nextValue.length || selectedWorkerIds.length));
    }
  };

  const handleServiceSetModeChange = (mode) => {
    if (mode === serviceSetMode) {
      return;
    }

    setServiceSetMode(mode);

    if (mode === entityModes.select) {
      setFieldsValue({
        newServiceSetName: '',
        newServiceSetComment: '',
        newServiceSetReasonIds: [],
        [serviceSetFilterFieldNames.service]: [],
        [serviceSetFilterFieldNames.selection]: '',
      });
    }
  };

  const buildServiceSet = ({
    id,
    serviceSetName,
    serviceSetComment,
    serviceIds,
    reasonIds,
    reasonGroups = serviceReasonGroups,
  }) => {
    const trimmedServiceSetName = (serviceSetName ?? '').trim();
    const reasonKeys = getValidSelectedValues(
      reasonIds || [],
      reasonGroups.flatMap((service) => service.reasons)
    );
    const serviceKeys = getValidSelectedValues(serviceIds || [], reasonGroups);

    if (
      !trimmedServiceSetName ||
      (!reasonKeys.length && !serviceKeys?.length)
    ) {
      return null;
    }

    const {
      groups: selectedGroups,
      requestParentTypeId,
      requestTypeId,
    } = getNormalizedServiceSetSelection({
      reasonGroups,
      selectedReasonKeys: reasonKeys,
      selectedServiceIds: serviceKeys,
    });
    const services = selectedGroups.map(({ service, selectedReasons }) => {
      const reasons = selectedReasons.map((reason) => ({
        id: reason.reasonId,
        isImmediate: reason.isImmediate ?? Boolean(reason.data?.isImmediate),
        name: reason.text || reason.reasonId,
      }));

      return {
        serviceId: service.value,
        serviceName: service.text,
        reasonIds: selectedReasons.map((reason) => reason.reasonId),
        reasonNames: reasons.map((reason) => reason.name),
        reasons,
        allReasonIds: service.reasons.map((reason) => reason.reasonId),
      };
    });

    return {
      id,
      name: trimmedServiceSetName,
      comment: (serviceSetComment ?? '').trim(),
      requestParentTypeId,
      requestTypeId,
      services,
    };
  };

  const handleCreateServiceSet = async (values = {}) => {
    const serviceSet = buildServiceSet({
      serviceSetName: values.serviceSetName ?? newServiceSetName,
      serviceSetComment:
        values.serviceSetComment ?? getFieldValue('newServiceSetComment'),
      serviceIds: values.serviceIds,
      reasonIds: values.reasonIds || getFieldValue('newServiceSetReasonIds'),
      reasonGroups: values.serviceReasonGroups,
    });

    if (!serviceSet) {
      return null;
    }

    const existingServiceSetIds = new Set(
      serviceSets.map((item) => String(item.id))
    );
    const response = await createServiceSetMutation.mutateAsync(serviceSet);
    const refreshedServiceSets = await serviceNamesQuery.refetch();
    const createdServiceSet =
      response?.id !== undefined && response?.id !== null
        ? { ...serviceSet, id: response.id }
        : findCreatedServiceSet({
            existingServiceSetIds,
            serviceSetName: serviceSet.name,
            serviceSets: refreshedServiceSets.data || [],
          });
    const savedServiceSet = createdServiceSet
      ? {
          ...createdServiceSet,
          comment: createdServiceSet.comment ?? serviceSet.comment,
          services: serviceSet.services,
        }
      : null;

    if (savedServiceSet?.id === undefined || savedServiceSet?.id === null) {
      message.error('Не удалось получить созданный набор услуг');
      await invalidateServiceSets();
      setFieldsValue({
        newServiceSetName: '',
        newServiceSetComment: '',
        newServiceSetReasonIds: [],
        [serviceSetFilterFieldNames.service]: [],
        [serviceSetFilterFieldNames.selection]: '',
      });
      setServiceSetMode(entityModes.select);
      return null;
    }

    queryClient.setQueryData(
      workScheduleQueryKeys.serviceDetails(savedServiceSet.id),
      savedServiceSet.services
    );
    setCreatedServiceSets((prevState) =>
      getUniqueServiceSets([savedServiceSet, ...prevState])
    );
    setSelectedServiceSets([savedServiceSet.id]);
    if (typeof onDirtyChange === 'function') {
      onDirtyChange(true);
    }
    setFieldsValue({
      newServiceSetName: '',
      newServiceSetComment: '',
      newServiceSetReasonIds: [],
      [serviceSetFilterFieldNames.service]: [],
      [serviceSetFilterFieldNames.selection]: '',
    });
    setServiceSetMode(entityModes.select);
    await queryClient.invalidateQueries(
      workScheduleQueryKeys.servicesWithTree(savedServiceSet.id)
    );
    await invalidateServiceSets();
    return savedServiceSet;
  };

  const handleUpdateServiceSet = async (serviceSetId, values = {}) => {
    const serviceSet = buildServiceSet({
      id: serviceSetId,
      serviceSetName: values.serviceSetName,
      serviceSetComment: values.serviceSetComment,
      serviceIds: values.serviceIds,
      reasonIds: values.reasonIds,
      reasonGroups: values.serviceReasonGroups,
    });

    if (!serviceSet) {
      return null;
    }

    const response = await updateServiceSetMutation.mutateAsync(serviceSet);
    const savedServiceSet = {
      ...serviceSet,
      id: response?.id ?? serviceSet.id,
    };

    queryClient.setQueryData(
      workScheduleQueryKeys.serviceDetails(savedServiceSet.id),
      savedServiceSet.services
    );
    setCreatedServiceSets((prevState) =>
      getUniqueServiceSets([
        savedServiceSet,
        ...prevState.filter((item) => String(item.id) !== String(serviceSetId)),
      ])
    );
    setSelectedServiceSets((prevState) =>
      getUniqueValues([...prevState, savedServiceSet.id])
    );
    if (typeof onDirtyChange === 'function') {
      onDirtyChange(true);
    }
    setFieldsValue({
      newServiceSetName: '',
      newServiceSetComment: '',
      newServiceSetReasonIds: [],
      [serviceSetFilterFieldNames.service]: [],
      [serviceSetFilterFieldNames.selection]: '',
    });
    setServiceSetMode(entityModes.select);
    await queryClient.invalidateQueries(
      workScheduleQueryKeys.servicesWithTree(savedServiceSet.id)
    );
    await invalidateServiceSets();
    return savedServiceSet;
  };

  const handleDeleteServiceSet = async (serviceSetId) => {
    if (serviceSetId === undefined || serviceSetId === null) {
      return { success: false };
    }

    await deleteServiceSetMutation.mutateAsync({ id: serviceSetId });
    const isSelectedServiceSetDeleted = selectedServiceSets.some(
      (id) => String(id) === String(serviceSetId)
    );

    setCreatedServiceSets((prevState) =>
      prevState.filter(
        (serviceSet) => String(serviceSet.id) !== String(serviceSetId)
      )
    );
    setSelectedServiceSets((prevState) =>
      prevState.filter((id) => String(id) !== String(serviceSetId))
    );
    queryClient.removeQueries(
      workScheduleQueryKeys.serviceDetails(serviceSetId)
    );
    queryClient.removeQueries(
      workScheduleQueryKeys.servicesWithTree(serviceSetId)
    );
    if (isSelectedServiceSetDeleted && typeof onDirtyChange === 'function') {
      onDirtyChange(true);
    }
    setServiceSetMode(entityModes.select);
    await invalidateServiceSets();
    return { success: true };
  };

  return {
    workerIds: selectedWorkerIds,
    serviceSetIds: selectedServiceSets,
    workers: {
      selectedWorkers,
      options: workerOptions,
      specialityOptions: workerSpecialityOptions,
      roleOptions: workerRoleOptions,
      loading: executersQuery.isFetching,
      onAdd: handleAddWorker,
      onAddMany: handleAddWorkers,
      onDelete: handleDeleteWorker,
      onDeleteMany: handleDeleteWorkers,
    },
    serviceSets: {
      mode: serviceSetMode,
      isCreateMode: isCreateServiceSetMode,
      isEditMode: isEditServiceSetMode,
      isDeleteMode: isDeleteServiceSetMode,
      selectedIds: selectedServiceSets,
      selectedItems: selectedServiceSetItems,
      items: serviceSets,
      options: serviceSetOptions,
      serviceOptions,
      serviceReasonGroups,
      servicesLoading:
        parentServicesQuery.isFetching || serviceNamesQuery.isFetching,
      serviceDetailsLoading: serviceDetailsQuery.isFetching,
      reasonsLoading: reasonsQuery.isFetching,
      createLoading: createServiceSetMutation.isLoading,
      updateLoading: updateServiceSetMutation.isLoading,
      deleteLoading: deleteServiceSetMutation.isLoading,
      getReasonKeysFromServiceSet,
      onChange: handleServiceSetsChange,
      onCreate: handleCreateServiceSet,
      onUpdate: handleUpdateServiceSet,
      onDelete: handleDeleteServiceSet,
      onModeChange: handleServiceSetModeChange,
    },
  };
};
